/**
 * Field Sync Helper
 * Handles field value synchronization when a field has syncFields defined
 * When a field with syncFields changes, copies its value to all target fields
 */

export class FieldSyncHelper {
  static syncMappings = new Map(); // sourceFieldId -> targetFieldIds[]
  static isSyncing = new Set(); // Track fields currently being synced to prevent loops

  /**
   * Setup field sync for a source field
   * @param {string} sourceFieldId - Source field ID
   * @param {Array} targetFieldIds - Target field IDs to sync to
   */
  static setup(sourceFieldId, targetFieldIds) {
    if (!targetFieldIds || !Array.isArray(targetFieldIds) || targetFieldIds.length === 0) {
      return;
    }

    // Store configuration
    this.syncMappings.set(sourceFieldId, targetFieldIds);

    // Setup event delegation if not already set up
    if (!this.delegationSetup) {
      this.setupEventDelegation();
      this.delegationSetup = true;
    }
  }

  /**
   * Setup event delegation for all synced fields
   */
  static setupEventDelegation() {
    const handler = (e) => {
      const fieldName = e.target.name || e.target.id;
      if (!fieldName) return;

      // Check if this field is a source field with syncFields
      const targetFieldIds = this.syncMappings.get(fieldName);
      if (targetFieldIds && targetFieldIds.length > 0) {
        // Prevent infinite loops
        if (this.isSyncing.has(fieldName)) {
          return;
        }

        this.syncToTargets(fieldName, e.target);
      }
    };

    document.addEventListener('input', handler, true);
    document.addEventListener('change', handler, true);
  }

  /**
   * Run initial sync for all source→target mappings (e.g. after form load or populateFormData)
   */
  static runInitialSync() {
    this.syncMappings.forEach((targetFieldIds, sourceFieldId) => {
      const sourceField = document.querySelector(`[name="${sourceFieldId}"], [id="${sourceFieldId}"]`);
      if (sourceField && targetFieldIds && targetFieldIds.length > 0) {
        this.syncToTargets(sourceFieldId, sourceField);
      }
    });
  }

  /**
   * Sync source field value to all target fields
   * @param {string} sourceFieldId - Source field ID
   * @param {HTMLElement} sourceField - Source field element
   */
  static syncToTargets(sourceFieldId, sourceField) {
    const targetFieldIds = this.syncMappings.get(sourceFieldId);
    if (!targetFieldIds || targetFieldIds.length === 0) {
      return;
    }

    // Mark as syncing to prevent loops
    this.isSyncing.add(sourceFieldId);

    try {
      const sourceValue = this.getValue(sourceField);

      targetFieldIds.forEach(targetFieldId => {
        // Skip if target field is the same as source (shouldn't happen, but safety check)
        if (targetFieldId === sourceFieldId) {
          return;
        }

        const targetField = document.querySelector(`[name="${targetFieldId}"], [id="${targetFieldId}"]`);
        if (!targetField) {
          return;
        }

        // Allow syncing to readonly targets (they are display copies of the source value)
        // Don't sync if target is currently being synced (prevent loops)
        if (this.isSyncing.has(targetFieldId)) {
          return;
        }

        // Set value based on field type
        this.setValue(targetField, sourceValue);

        // Trigger change event to ensure other handlers (like conditions) are notified
        targetField.dispatchEvent(new Event('input', { bubbles: true }));
        targetField.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } finally {
      // Remove from syncing set after a short delay to allow events to propagate
      setTimeout(() => {
        this.isSyncing.delete(sourceFieldId);
      }, 0);
    }
  }

  /**
   * Get value from a field based on its type
   * @param {HTMLElement} field - Field element
   * @returns {string|Array} Field value
   */
  static getValue(field) {
    if (field.tagName === 'SELECT') {
      // For select, return the selected value
      return field.value || '';
    } else if (field.type === 'checkbox' || field.type === 'radio') {
      // For checkboxes/radios, return checked state
      return field.checked ? field.value : '';
    } else if (field.type === 'file') {
      // For file inputs, we can't sync the file itself, so skip
      return '';
    } else {
      // For text inputs, textarea, etc.
      return field.value || '';
    }
  }

  /**
   * Set value to a field based on its type
   * @param {HTMLElement} field - Target field element
   * @param {string|Array} value - Value to set
   */
  static setValue(field, value) {
    if (field.tagName === 'SELECT') {
      // For select, set the value and trigger change
      field.value = value;
    } else if (field.type === 'checkbox' || field.type === 'radio') {
      // For checkboxes/radios, check if value matches
      if (Array.isArray(value)) {
        field.checked = value.includes(field.value);
      } else {
        field.checked = field.value === value;
      }
    } else if (field.type === 'file') {
      // For file inputs, we can't sync files, so skip
      return;
    } else {
      // For text inputs, textarea, number, date, email, phone, etc.
      field.value = value;
    }
  }

  /**
   * Clear all sync mappings (useful for cleanup)
   */
  static clear() {
    this.syncMappings.clear();
    this.isSyncing.clear();
    this.delegationSetup = false;
  }
}
